import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';

import { TranslatePipe } from '@core/i18n-legacy';
import type { TimelineEvent } from '@core/tellent-assistant-util';
import { ButtonComponent, IconComponent, SpinnerComponent } from '@core/user-interface-legacy';

import { TellentAssistantBrandMarkComponent } from '../../brand-mark/tellent-assistant-brand-mark.component';
import { TellentAssistantTextPartComponent } from '../text-part/text-part.component';

export type ThinkingRunState = 'running' | 'done';

@Component({
  selector: 'tellent-assistant-thinking-section',
  imports: [
    ButtonComponent,
    IconComponent,
    SpinnerComponent,
    TellentAssistantBrandMarkComponent,
    TellentAssistantTextPartComponent,
    TranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './thinking-section.component.html',
  styleUrl: './thinking-section.component.less',
})
export class TellentAssistantThinkingSectionComponent {
  protected readonly expanded = signal<boolean>(false);

  protected readonly toolCount = computed(
    () => this.timeline().filter(event => event.kind === 'tool').length,
  );

  protected readonly hasContent = computed(() => this.timeline().length > 0);

  protected readonly latestRunningTool = computed(() => {
    const events = this.timeline();
    const last = events[events.length - 1];
    return last?.kind === 'tool' && last.status === 'running' ? last : undefined;
  });

  protected readonly triggerLabelKey = computed(() => {
    if (this.runState() === 'done') return 'tlc.tellent_assistant.tool_call.steps_completed';
    return this.latestRunningTool() ? null : 'tlc.tellent_assistant.tool_call.thinking_status';
  });

  protected readonly triggerLabelToolName = computed(() => this.latestRunningTool()?.name ?? null);

  protected readonly showThinkingShimmer = computed(() => {
    if (this.runState() !== 'running') return false;
    return !this.latestRunningTool();
  });

  public readonly timeline = input.required<readonly TimelineEvent[]>();
  public readonly runState = input.required<ThinkingRunState>();

  protected toggle(): void {
    this.expanded.update(v => !v);
  }
}
